import axios from 'axios';
import { ZIGGEO_BASE_URL, ZIGGEO_API_KEY } from 'src/config/common.constants';
import { API_RESPONSE_CODE } from 'src/customer-call-sdk/config/constants';
import END_POINT from '../config/endpoints.constants';
import { getDetailedErrorMessage, getErrorMessage } from '../customer-call-sdk/lib/API';
import { VriAPIResponse, VriAPIResult, VideoMail } from '../types';

export interface GetAllVideoMailResult {
    videoMails?: any[];
    error?: any;
}

interface GetAllVideoMailResponse {
    data: {
        videoEmails: VideoMail[];
    };
    code: number;
    message: string;
}

export async function getAllVideoMail(): Promise<GetAllVideoMailResult> {
    const result = {} as GetAllVideoMailResult;

    try {
        const { data: responseData } = await axios.post<GetAllVideoMailResponse>(
            END_POINT.GET_VIDEOMAILS,
        );
        if (responseData?.data) {
            const { videoEmails } = responseData.data;
            result.videoMails = videoEmails;
        }
    } catch (error) {
        result.error = error;
    }
    return result;
}

export interface SendVideoMailOptions {
    requestId: string;
    videoToken: string;
    durationInMilliSeconds: number;
}
export interface SendVideoMailResponse extends VriAPIResponse { }

export interface SendVideoMailResult extends VriAPIResult {
    data?: {
        message: string;
    };
}

export const sendVideoMail = async (
    options: SendVideoMailOptions,
): Promise<SendVideoMailResult> => {
    const result: SendVideoMailResult = {};
    try {
        const { data: responseData } = await axios.post<SendVideoMailResponse>(
            END_POINT.SEND_VIDEO_MAIL,
            options,
        );
        if (responseData?.code === 0) {
            result.data = { message: responseData.message };
        } else {
            result.error = responseData.message;
        }
    } catch (err) {
        result.error = getErrorMessage(err);
    }
    return result;
};

export interface SendVideoMailForMobileOptions {
    requestId: string;
    videoToken: string;
    durationInMilliSeconds: number;
    accessToken: any;
}

export const sendVideoMailForMobile = async (
    options: SendVideoMailForMobileOptions,
): Promise<SendVideoMailResult> => {
    const result: SendVideoMailResult = {};
    let responseData: any = {};
    try {
        if (options.accessToken) {
            const instance = axios.create();
            instance.interceptors.request.use(
                (config) => {
                    config.headers.Authorization = `Bearer ${options.accessToken}`;
                    config.headers['cache-control'] = 'no-cache';
                    config.headers['App-Name'] = 'userapp';
                    config.headers['App-Source'] = 'userapp';
                    return config;
                },
                (error) => {
                    return Promise.reject(error);
                },
            );

            responseData = await instance.post<SendVideoMailResponse>(
                END_POINT.SEND_VIDEO_MAIL,
                options,
            );
        } else {
            responseData = await axios.post<SendVideoMailResponse>(
                END_POINT.SEND_VIDEO_MAIL,
                options,
                {
                    headers: {
                        'App-Name': 'userapp',
                        'App-Source': 'userapp',
                    }
                }
            );
        }
        if (responseData?.code === 0) {
            result.data = { message: responseData.message };
        } else {
            result.error = responseData.message;
        }
    } catch (err) {
        result.error = getDetailedErrorMessage(err);
    }
    return result;
};

export interface GetDurationResponse extends VriAPIResponse {
    status: string;
    responseText?: {
        duration: number;
    };
}

export interface GetDurationResult extends VriAPIResult {
    duration: number;
}

export const GetDurationFromZiggeo = async (videoToken: string) => {
    const URL = `${ZIGGEO_BASE_URL}/v1/applications/${ZIGGEO_API_KEY}/videos/${videoToken}?_wrapstatus=true`;

    const result = {} as GetDurationResult;
    const newInstance = axios.create();

    try {
        const { data: responseData } = await newInstance.get<GetDurationResponse>(URL);
        if (responseData?.responseText) {
            result.duration = responseData?.responseText?.duration;
        } else {
            result.error = responseData.message;
        }
    } catch (error) {
        result.error = error;
    }
    return result;
};

export interface DeleteVideoMailResult extends VriAPIResult {
    code?: number;
    message?: string;
}

export const deleteVideoMail = async (videoEmailId: string): Promise<DeleteVideoMailResult> => {
    const result: DeleteVideoMailResult = {};

    try {
        const { data: responseData } = await axios.post(`${END_POINT.DELETE_VIDEOMAIL}`, {
            videoEmailId,
        });

        result.code = responseData.code;
        result.message = responseData.message;

        if (responseData.code !== API_RESPONSE_CODE.SUCCESS) {
            result.error = responseData.message;
        }
    } catch (err) {
        result.error = getDetailedErrorMessage(err);
    }

    return result;
};
